/* eslint-disable react/display-name */
import React, { memo, useState, useCallback } from 'react';
import { Select, Form, Divider, Space, Typography } from 'antd';
import { FileLabelResponseDto } from '@/shared/api';
import { OrganizationFileResponse } from '@/shared/types/file';

interface LabelsSelectProps {
  file: OrganizationFileResponse;
  labels: Array<FileLabelResponseDto>;
  onLabelSelect?: (fileId: string, labelId: string) => Promise<void>;
  onLabelDeselect?: (fileId: string, labelId: string) => Promise<void>;
  onLabelCreate?: (name: string) => Promise<FileLabelResponseDto>;
}

export const LabelsSelect = memo(
  ({
    file,
    labels,
    onLabelSelect,
    onLabelDeselect,
    onLabelCreate,
  }: LabelsSelectProps) => {
    const [searchValue, setSearchValue] = useState('');

    const getDropdownRender = useCallback(
      (menu: React.ReactNode) => (
        <>
          {menu}
          <Divider style={{ margin: '8px 0' }} />
          <Space
            direction="vertical"
            style={{ padding: '0 8px 4px', width: '100%' }}
          >
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              Type to create new labels
            </Typography.Text>
          </Space>
        </>
      ),
      [],
    );

    const getOptions = useCallback(
      (labels: FileLabelResponseDto[], searchValue: string) => [
        ...labels.map((tag) => ({
          label: tag.name,
          value: tag.id,
        })),
        ...(searchValue &&
        !labels.find((t) => t.name.toLowerCase() === searchValue.toLowerCase())
          ? [
              {
                label: `Create "${searchValue}"`,
                value: `new-${searchValue}`,
              },
            ]
          : []),
      ],
      [],
    );

    const handleSelect = useCallback(
      async (value: string) => {
        if (value.startsWith('new-') && onLabelCreate && onLabelSelect) {
          const newLabel = await onLabelCreate(value.slice(4));
          await onLabelSelect(file.id, newLabel.id);
        } else if (onLabelSelect) {
          await onLabelSelect(file.id, value);
        }
      },
      [file.id, onLabelCreate, onLabelSelect],
    );

    const handleDeselect = useCallback(
      async (value: string) => {
        if (onLabelDeselect) {
          await onLabelDeselect(file.id, value);
        }
      },
      [file.id, onLabelDeselect],
    );

    return (
      <Form.Item style={{ margin: 0 }}>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Filter by labels"
          optionFilterProp="label"
          allowClear={false}
          removeIcon={null}
          maxTagCount="responsive"
          dropdownRender={getDropdownRender}
          options={getOptions(labels, searchValue)}
          value={file.labels?.map((label) => label.id) || []}
          onSearch={setSearchValue}
          onSelect={handleSelect}
          onDeselect={handleDeselect}
        />
      </Form.Item>
    );
  },
);
